import { Box, Button, Grid, Link, Typography } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { Alert, AlertTitle } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import Loading from "../../../Shared/loading";
import { setCameraAndMicPermissionStatus, setLocationPermissionStatus } from "../../redux/actions/permissionActions";
import { ApplicationData } from "../../types";
import { handleGetUserMediaError } from "../../../Shared/getUserMediaHelper";
import { Localizations } from "../../../Shared/sharedTypes";

const mapState = function ({ order, permissions }: ApplicationData) {
    return {
        order,
        permissions
    };
};

const mapDispatch = {
    setCameraAndMicPermissionStatus,
    setLocationPermissionStatus,
};
const connector = connect(mapState, mapDispatch);
type PropsFromRedux = ConnectedProps<typeof connector>;
type PropTypes = PropsFromRedux & {
    nextPhase: () => void;
    deniedAccess: () => void;
    localizations: Localizations;
};

declare global {
    interface Window {
        Modernizr: ModernizrStatic;
    }
}

export function Permissions({
    order,
    permissions,
    setCameraAndMicPermissionStatus,
    setLocationPermissionStatus,
    nextPhase,
    deniedAccess,
    localizations,
}: PropTypes) {
    const [startedCheck, markCheckStarted] = useState<boolean>(false);
    const [videoCheckCompleted, updateVideoCheck] = useState<boolean>(false);
    const [locationCheckCompleted, updateLocationCheck] = useState<boolean>(false);
    //const { Modernizr } = window as any;
    useEffect(() => {
        console.log("GEO: " + Modernizr.geolocation);
        if (!startedCheck) {
            markCheckStarted(true);
            try {
                navigator.permissions
                    .query({ name: "microphone" })
                    .then((permissionObj: PermissionStatus) => {
                        if (permissionObj.state !== permissions.haveCameraAccess) {
                            setCameraAndMicPermissionStatus(permissionObj.state);
                        }
                        updateVideoCheck(true);
                    })
                    .catch((e) => {
                        //setCameraAndMicPermissionStatus("prompt");
                        updateVideoCheck(true);
                    });
            } catch (e) {
                //setCameraAndMicPermissionStatus("prompt");
                updateVideoCheck(true);
            }

            try {
                navigator.permissions
                    .query({ name: "geolocation" })
                    .then((permissionObj: PermissionStatus) => {
                        if (permissionObj.state !== permissions.haveLocationAccess) {
                            setLocationPermissionStatus(permissionObj.state);
                        }
                        updateLocationCheck(true);
                    })
                    .catch((e) => {
                        //setLocationPermissionStatus("prompt");
                        updateLocationCheck(true);
                    });
            } catch (e) {
                updateLocationCheck(true);
            }
        }
        if (permissions.haveLocationAccess === "granted" && permissions.haveCameraAccess === "granted") {
            nextPhase();
        }
    }, [permissions]);

    function promptForCameraAccess() {
        if (navigator.mediaDevices) {
            navigator.mediaDevices
                .getUserMedia({ video: true, audio: true })
                .then((mediaStream) => {
                    try {
                        mediaStream.getVideoTracks().forEach((t) => t.stop());
                        mediaStream.getAudioTracks().forEach((t) => t.stop());
                        mediaStream.stop();
                    } catch (e) {}
                    setCameraAndMicPermissionStatus("granted");
                })
                .catch((err: Error) => handleGetUserMediaError(err, localizations, setCameraAndMicPermissionStatus));
        } else {
            alert(localizations.browserOutOfDate);
        }
    }

    function promptForLocationAccess() {
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setLocationPermissionStatus("granted");
            },
            (error) => {
                setLocationPermissionStatus("denied");
            }
        );
    }

    if (!videoCheckCompleted || !locationCheckCompleted) {
        return <Loading message={localizations.verifyingPermissions}></Loading>;
    } else {
        return (
            <>
                <Grid item className="PhaseTopSection">
                    <Box m={3}>
                        <Typography variant="h5">{localizations.PermissionsHeader}</Typography>

                        <Typography variant="body1">{localizations.PermissionsBody}</Typography>
                        <Box m={2}>
                            <Box pb={2}>
                                {permissions.haveCameraAccess === "prompt" && (
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        fullWidth
                                        onClick={promptForCameraAccess}
                                    >
                                        {localizations.AllowVideoAndMicrophone}
                                    </Button>
                                )}
                                {permissions.haveCameraAccess === "granted" && (
                                    <Alert severity="success">
                                        <AlertTitle>{localizations.cameraAndMicrophone}</AlertTitle>
                                        {localizations.permissionGranted}
                                    </Alert>
                                )}
                                {permissions.haveCameraAccess === "denied" && (
                                    <Button
                                        color="secondary"
                                        variant="outlined"
                                        fullWidth
                                        startIcon={<Close />}
                                        onClick={promptForCameraAccess}
                                    >
                                        {localizations.AllowVideoAndMicrophone}
                                    </Button>
                                )}
                            </Box>
                            {navigator.geolocation && order.customer.validateExternalUserLocationDistance && (
                                <>
                                    {permissions.haveLocationAccess === "prompt" && (
                                        <Button
                                            color="primary"
                                            fullWidth
                                            variant="contained"
                                            onClick={promptForLocationAccess}
                                            disabled={permissions.haveCameraAccess !== "granted"}
                                        >
                                            {localizations.AllowLocationService}
                                        </Button>
                                    )}
                                    {permissions.haveLocationAccess === "granted" && (
                                        <Alert severity="success">
                                            <AlertTitle>{localizations.locationService}</AlertTitle>
                                            {localizations.permissionGranted}
                                        </Alert>
                                    )}
                                    {permissions.haveLocationAccess === "denied" && (
                                        <>
                                            <Button
                                                color="secondary"
                                                variant="outlined"
                                                fullWidth
                                                startIcon={<Close />}
                                                onClick={promptForLocationAccess}
                                            >
                                                {localizations.AllowLocationService}
                                            </Button>
                                        </>
                                    )}
                                </>
                            )}
                        </Box>
                        <Button
                            href="#"
                            variant="text"
                            hidden={
                                permissions.haveCameraAccess !== "denied" && permissions.haveLocationAccess !== "denied"
                            }
                            onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                                e.preventDefault();
                                deniedAccess();
                            }}
                            color="secondary"
                            size="small"
                        >
                            {localizations.clickDenyByMistake}
                        </Button>
                    </Box>
                </Grid>
                <Grid item className="footer">
                    <Button
                        size="large"
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={() => nextPhase()}
                        disabled={
                            permissions.haveCameraAccess !== "granted"
                        }
                    >
                        {localizations.next}
                    </Button>
                </Grid>
            </>
        );
    }
}

export default connector(Permissions);
